import { useCallback, useState } from "react";
import { errorMessage, fileDownloadResponse } from "helpers/common";
import { api } from "helpers/axios";
import { ActionMenu } from "components/ui";
import { useTranslation } from "translation/useTranslation";

/**
 * The one download menu of a finished import: the full report (every file row with its result, plus a summary
 * sheet in XLSX) and, when `rejectedFilter` is set, the same report limited to the rows that still need fixing.
 * `endpoint` is the import's report export URL, e.g. `/employee-imports/{id}/report/export`.
 */
export default function ImportReportMenu({ endpoint, fallbackFilename, rejectedFilter = null, rejectedLabel, onError, className = "" }) {
  const { editableTranslate } = useTranslation();
  const [exportingKey, setExportingKey] = useState(null);

  const handleDownload = useCallback(async (key, fileType, filter = {}) => {
    setExportingKey(key);
    try {
      const response = await api.get(endpoint, {
        params: { file_type: fileType, ...filter },
        responseType: "blob",
      });
      fileDownloadResponse(response, `${fallbackFilename}.${fileType}`);
    } catch (err) {
      onError?.(errorMessage(err, "download import report"));
    } finally {
      setExportingKey(null);
    }
  }, [endpoint, fallbackFilename, onError]);

  const items = [
    { key: "full", heading: true, label: editableTranslate("import_report.group_full", "Vollständiger Bericht") },
    {
      key: "xlsx",
      label: editableTranslate("import_report.format_xlsx_summary", "Excel (mit Zusammenfassung)"),
      onClick: () => handleDownload("xlsx", "xlsx"),
    },
    {
      key: "csv",
      label: editableTranslate("import_report.format_csv", "CSV"),
      onClick: () => handleDownload("csv", "csv"),
    },
  ];

  if (rejectedFilter) {
    items.push(
      { key: "rejected", heading: true, label: rejectedLabel },
      {
        key: "rejected-xlsx",
        label: editableTranslate("import_report.format_xlsx", "Excel"),
        onClick: () => handleDownload("rejected-xlsx", "xlsx", rejectedFilter),
      },
      {
        key: "rejected-csv",
        label: editableTranslate("import_report.format_csv", "CSV"),
        onClick: () => handleDownload("rejected-csv", "csv", rejectedFilter),
      },
    );
  }

  return (
    <ActionMenu
      className={className}
      label={editableTranslate("import_report.download", "Bericht herunterladen")}
      triggerLabel={editableTranslate("import_report.download", "Bericht herunterladen")}
      triggerIcon="bi-file-earmark-arrow-down"
      triggerVariant="secondary"
      triggerSize="sm"
      loading={!!exportingKey}
      disabled={!!exportingKey}
      testIdPrefix="import-report-"
      items={items}
    />
  );
}
